import { useEffect, useState, useMemo } from "react";
import { useOasisBackend } from "../hooks/useOasisBackend";
import { ReadDatabase } from "../api/OasisBackendApi";
import Modal from "../testsuite/components/modal";
import { useNavigate, Link } from "react-router-dom";
import TesseractLoader from "./ui/tesseract-loader";
import { toast, ToastContainer } from "react-toastify";
import { useUser } from "../hooks/useUser";

// Sorting Options
type DatabaseSortOption = "created-desc" | "created-asc" | "name-asc" | "name-desc" | "owner-asc" | "owner-desc" | "visibility-asc" | "visibility-desc";

const sortOptions: { label: string; value: DatabaseSortOption }[] = [
    { label: "Newest First", value: "created-desc" },
    { label: "Oldest First", value: "created-asc" },
    { label: "Name (A-Z)", value: "name-asc" },
    { label: "Name (Z-A)", value: "name-desc" },
    { label: "Owner (A-Z)", value: "owner-asc" },
    { label: "Owner (Z-A)", value: "owner-desc" },
    { label: "Visibility (Public First)", value: "visibility-desc" },
    { label: "Visibility (Private First)", value: "visibility-asc" }
];

const compareFunctions: Record<DatabaseSortOption, (a: ReadDatabase, b: ReadDatabase) => number> = {
    "created-desc": (a, b) => {
        return new Date(b.created_at || "").getTime() - new Date(a.created_at || "").getTime();
    },
    "created-asc": (a, b) => {
        return new Date(a.created_at || "").getTime() - new Date(b.created_at || "").getTime();
    },
    "name-asc": (a, b) => a.name.localeCompare(b.name),
    "name-desc": (a, b) => b.name.localeCompare(a.name),
    "owner-asc": (a, b) => (a.owner || "").localeCompare(b.owner || ""),
    "owner-desc": (a, b) => (b.owner || "").localeCompare(a.owner || ""),
    "visibility-asc": (a, b) => Number(a.is_public) - Number(b.is_public),
    "visibility-desc": (a, b) => Number(b.is_public) - Number(a.is_public),
};

const CreateDatabaseForm = ({successCallback}: {successCallback: () => void}) => {
    const db = useOasisBackend();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const navigate = useNavigate();

    const handleCreate = async () => {
        setCreateLoading(true);
        setCreateError(null);

        if (name === "") {
            setCreateError("Name is required");
            setCreateLoading(false);
            return;
        }
        if (description === "") {
            setCreateError("Description is required");
            setCreateLoading(false);
            return;
        }

        try {
            const response = await db.endpoints.api.apiDatabasesCreate({
                name,
                description
            });

            if (response.status === 201) {
                toast.success("Knowledge base created successfully");
                navigate(`/knowledge-base/${response.data.id}`);
                successCallback();
            } else {
                setCreateError("Failed to create knowledge base");
                toast.error("Failed to create knowledge base");
            }
        } catch (error) {
            setCreateError("Failed to create knowledge base");
            toast.error("Failed to create knowledge base");
        } finally {
            setCreateLoading(false);
        }
    }

    return (
        <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Create Knowledge Base</h2>

            {createError && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-red-400 text-sm">{createError}</p>
                </div>
            )}

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Knowledge Base Name
                    </label>
                    <input 
                        onChange={(e) => setName(e.target.value)} 
                        value={name} 
                        type="text" 
                        placeholder="Enter knowledge base name"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors" 
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Description
                    </label>
                    <textarea 
                        onChange={(e) => setDescription(e.target.value)} 
                        value={description} 
                        placeholder="Enter knowledge base description"
                        rows={4}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical" 
                    />
                </div>

                <button 
                    className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800" 
                    onClick={handleCreate}
                    disabled={createLoading}
                >
                    {createLoading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating...
                        </div>
                    ) : (
                        "Create Knowledge Base"
                    )}
                </button>
            </div>
        </div>
    )
}

const CreateDatabaseModal = () => {
    const [showModal, setShowModal] = useState(false);
    return (
        <div>
            <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                <CreateDatabaseForm successCallback={() => setShowModal(false)} />
            </Modal>
            <button 
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800" 
                onClick={() => setShowModal(true)}
            >
                Create Knowledge Base
            </button>
        </div>
    )
}

const ListAndCreateDatabases = () => {
    const user = useUser();
    const db = useOasisBackend();
    const [Databases, setDatabases] = useState<ReadDatabase[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortBy, setSortBy] = useState<DatabaseSortOption>("created-desc");

    useEffect(() => {
        setIsLoading(true);
        const fetchDatabases = async () => {
            try {
                const response = await db.endpoints.api.apiDatabasesList();
                setDatabases(response.data);
            } catch (error) {
                console.error('Error fetching databases:', error);
                toast.error("Failed to fetch knowledge bases");
            } finally {
                setIsLoading(false);
            }
        }
        fetchDatabases();
    }, []);

    const deleteDatabase = async (Database: ReadDatabase) => {
        // Add a confirmation dialog
        const confirmed = window.confirm("Are you sure you want to delete this knowledge base? This action cannot be undone.");
        if (!confirmed) {
            return;
        }

        try {
            const response = await db.endpoints.api.apiDatabasesDestroy(Database.id);
            if (response.status === 204) {
                setDatabases(Databases.filter((kb) => kb.id !== Database.id));
                toast.success("Knowledge base deleted successfully");
            } else {
                toast.error("Failed to delete knowledge base");
            }
        } catch (error) {
            console.error('Error deleting database:', error);
            toast.error("Failed to delete knowledge base");
        }
    }

    // Memoize sorted databases
    const sortedDatabases = useMemo(() => {
        const arr = [...Databases];
        if (sortBy && compareFunctions[sortBy]) {
            arr.sort(compareFunctions[sortBy]);
        }
        return arr;
    }, [Databases, sortBy]);

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Knowledge Bases</h1>
                <p className="text-gray-400">Manage your knowledge bases and their content</p>
            </div>

            <div className="mb-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8 justify-between">
                <CreateDatabaseModal />
                {/* Sort options dropdown */}
                <div className="flex items-center gap-2">
                    <label htmlFor="sort-by" className="text-sm text-gray-300 whitespace-nowrap">Sort by:</label>
                    <select
                        id="sort-by"
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value as DatabaseSortOption)}
                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {sortOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <TesseractLoader />
                </div>
            ) : (
                <div className="space-y-4">
                    {sortedDatabases.length === 0 ? (
                        <div className="bg-gray-800 rounded-lg p-8 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">No knowledge bases yet</h3>
                            <p className="text-gray-400">Create your first knowledge base to get started</p>
                        </div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {sortedDatabases.map((Database) => (
                                <div key={Database.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                                    <div className="mb-4">
                                        <h3 className="text-lg font-semibold text-white mb-2">{Database.name}</h3>
                                        <p className="text-gray-400 text-sm line-clamp-3">{Database.description}</p>
                                    </div>

                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                                        <span>ID: {Database.id}</span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            Database.is_public ? 'bg-green-900/20 text-green-400' : 'bg-gray-700 text-gray-400'
                                        }`}>
                                            {Database.is_public ? 'Public' : 'Private'}
                                        </span>
                                    </div>
                                    {
                                        user.state === "LoggedIn" && user.user.email === Database.owner ? (
                                        <div className="flex gap-2">
                                            <Link 
                                                to={`/knowledge-base/${Database.id}`} 
                                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 text-center"
                                            >
                                                Manage
                                            </Link>
                                            <button 
                                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200" 
                                                onClick={() => deleteDatabase(Database)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                     ) : 
                                     (
                                        <div className="flex gap-2">
                                            <Link 
                                                to={`/knowledge-base/${Database.id}`} 
                                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 text-center"
                                            >
                                                Test
                                            </Link>
                                        </div>
                                     )
                                    }
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const Databases = () => {
    return (
        <div className="min-h-screen bg-gray-900 text-gray-100">
            <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />
            <ListAndCreateDatabases />
        </div>
    )
}

export default Databases;